import React, { useState } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography, Stack, Button, CircularProgress, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import SelectorClienteOpcional from './SelectorClienteOpcional';
import { colorPrimarioTramite } from './coloresTramite';
import { descargarPactoDocumento, descargarPactoBadaroDocumento } from '../../services/pactoService';

const NOMBRE_ARCHIVO_BADARO = 'BADAROS + 2 Diferencias + 30%.docx';

/** Los tres pactos que sabe generar el sistema, cada uno con su propia tarjeta. */
export const PACTO_TIPOS = {
    SIN_PORCENTAJE: {
        etiqueta: 'Pacto',
        descripcion: 'Compromiso de pago de honorarios',
        icono: 'bi-file-earmark-word',
        haberes: [1, 2, 3],
        conPorcentaje: false,
    },
    CON_PORCENTAJE: {
        etiqueta: 'Pacto + 30%',
        descripcion: 'Compromiso de pago con el 30% adicional',
        icono: 'bi-file-earmark-word',
        haberes: [2, 3],
        conPorcentaje: true,
    },
    BADARO: {
        etiqueta: 'Pacto Badaro',
        descripcion: 'BADAROS + 2 Diferencias + 30%',
        icono: 'bi-file-earmark-word',
    },
};

const SIN_PASO = 'CERRADO';
const PASO_CLIENTE = 'CLIENTE';
const PASO_HABERES = 'HABERES';

function descargarBlob(data, nombreArchivo) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

/**
 * Tarjeta de "Generar Documentos Automáticos" para UN pacto (`tipo`: SIN_PORCENTAJE,
 * CON_PORCENTAJE o BADARO — ver PACTO_TIPOS). Antes era una sola tarjeta "Pacto" con un
 * selector de tipo adentro; ahora cada tipo es su propio botón, así que ese paso desaparece y
 * el flujo queda igual de corto que el de las carátulas: cliente opcional → (haberes, salvo
 * Badaro que no los pide) → descarga.
 *
 * `PactoDocumentoService` (backend) ya trabaja por cliente directo, sin trámite. El cliente es
 * opcional acá: `cuil` viaja `undefined` cuando se sigue sin él, y el backend ya sabe imprimir
 * el pacto en blanco.
 */
export default function TarjetaGenerarPacto({ tipo }) {
    const config = PACTO_TIPOS[tipo];

    const [paso, setPaso] = useState(SIN_PASO);
    const [cuilElegido, setCuilElegido] = useState(null);
    const [descargando, setDescargando] = useState(false);
    const [error, setError] = useState(null);

    if (!config) {
        return null;
    }

    const esBadaro = tipo === 'BADARO';

    const cerrar = () => {
        setPaso(SIN_PASO);
        setError(null);
    };

    const handleCuilResuelto = (cliente) => {
        setCuilElegido(cliente?.cuil || undefined);
        if (esBadaro) {
            handleDescargarBadaro(cliente?.cuil || undefined);
        } else {
            setPaso(PASO_HABERES);
        }
    };

    const handleDescargar = async (numeroHaberes) => {
        setDescargando(true);
        setError(null);
        try {
            const response = await descargarPactoDocumento({
                cuil: cuilElegido,
                numeroHaberes,
                conPorcentaje: config.conPorcentaje,
            });
            const nombreArchivo = `PACTO X ${numeroHaberes} HABERES${config.conPorcentaje ? ' + 30%' : ''}.docx`;
            descargarBlob(response.data, nombreArchivo);
            toast.success(`Descargando ${nombreArchivo}`);
            cerrar();
        } catch (err) {
            console.error('❌ Error generando documento PACTO:', err);
            setError('No se pudo generar el documento PACTO.');
        } finally {
            setDescargando(false);
        }
    };

    const handleDescargarBadaro = async (cuil) => {
        setPaso(PASO_HABERES);
        setDescargando(true);
        setError(null);
        try {
            const response = await descargarPactoBadaroDocumento({ cuil });
            descargarBlob(response.data, NOMBRE_ARCHIVO_BADARO);
            toast.success(`Descargando ${NOMBRE_ARCHIVO_BADARO}`);
            cerrar();
        } catch (err) {
            console.error('❌ Error generando documento PACTO BADARO:', err);
            setError('No se pudo generar el documento PACTO.');
        } finally {
            setDescargando(false);
        }
    };

    return (
        <>
            <div
                className="acceso-card acceso-card-pacto"
                onClick={() => { setCuilElegido(null); setPaso(PASO_CLIENTE); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setCuilElegido(null);
                        setPaso(PASO_CLIENTE);
                    }
                }}
            >
                <i className={`bi ${config.icono} acceso-card-icon`}></i>
                <div className="acceso-card-body">
                    <h5>{config.etiqueta}</h5>
                    <p>{config.descripcion}</p>
                </div>
                <i className="bi bi-arrow-right-short acceso-card-arrow"></i>
            </div>

            <Dialog
                open={paso !== SIN_PASO}
                onClose={cerrar}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', overflowY: 'auto' } }}
            >
                <Box
                    sx={{
                        background: `${colorPrimarioTramite('pacto')}12`,
                        borderBottom: `1px solid ${colorPrimarioTramite('pacto')}33`,
                        px: 3, py: 2.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: `${colorPrimarioTramite('pacto')}1F`,
                                color: colorPrimarioTramite('pacto'),
                                fontSize: '1.3rem',
                            }}
                        >
                            <i className={`bi ${config.icono}`}></i>
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#1e293b' }}>
                                {config.etiqueta}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {paso === PASO_CLIENTE ? '¿Generamos para un cliente del sistema?' : config.descripcion}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={cerrar} size="small" sx={{ flexShrink: 0 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <DialogContent sx={{ pt: 3 }}>
                    {paso === PASO_CLIENTE && (
                        <SelectorClienteOpcional onContinuar={handleCuilResuelto} color={colorPrimarioTramite('pacto')} />
                    )}
                    {paso === PASO_HABERES && esBadaro && (
                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                            {descargando ? <CircularProgress size={20} /> : <span>Generando el documento...</span>}
                        </Stack>
                    )}
                    {paso === PASO_HABERES && !esBadaro && (
                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                            {config.haberes.map((numeroHaberes) => (
                                <Button
                                    key={numeroHaberes}
                                    variant="outlined"
                                    disabled={descargando}
                                    startIcon={descargando ? <CircularProgress size={16} /> : <i className="bi bi-download"></i>}
                                    onClick={() => handleDescargar(numeroHaberes)}
                                    sx={{
                                        borderRadius: '10px',
                                        textTransform: 'none',
                                        color: colorPrimarioTramite('pacto'),
                                        borderColor: colorPrimarioTramite('pacto'),
                                    }}
                                >
                                    X {numeroHaberes} HABERES{config.conPorcentaje ? ' + 30%' : ''}
                                </Button>
                            ))}
                        </Stack>
                    )}

                    {error && (
                        <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
